// Presentation + setup-form config for the built-in frameworks. Mirrors iOS
// BuiltInFrameworks.swift (icon/tagline/accent) and each *StartView's collected
// start_inputs. Backend `GET /frameworks/` is the source of truth for which
// frameworks exist + their live cost; this file only adds web presentation and
// the bespoke setup fields. Unknown slugs (user-authored) fall back to a generic
// field-driven form via `required_start_inputs` from preview-steps.

import type { StartInputs } from "@/api/create-types";

export interface FieldSpec {
  key: string;
  // `source` = the unified material control: a paste textarea WITH an inline
  // "Upload file(s)" button (PDF/DOCX/TXT, multi-select), both writing to this
  // field's key. Every framework that takes source material uses it so upload
  // UX is identical everywhere. `file` is the legacy standalone upload (kept
  // for back-compat / user-authored forms; appends to source_text).
  label: string;
  // `marked-file-picker` lets the user load a file they MARKED earlier (in any
  // project) into this run — it stores the chosen file_id and is emitted as
  // start_inputs.source_file_ids. The backend reads the file's marks + extracts
  // figures, driving the "lite project enrichment" sub-pipeline.
  kind:
    | "textarea"
    | "text"
    | "number"
    | "toggle"
    | "select"
    | "file"
    | "source"
    | "marked-file-picker";
  required?: boolean;
  placeholder?: string;
  hint?: string;
  options?: { value: string; label: string }[];
  default?: string | number | boolean;
  /** For kind:"file"/"source" — the accept attribute (e.g. ".pdf,.txt,.docx").
   *  Defaults to ".pdf,.txt,.md,.docx" for `source` when omitted. */
  accept?: string;
}

/** The 15 languages the app supports (ISO code → English display name). */
export const SUPPORTED_LANGUAGES: { value: string; label: string }[] = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "nl", label: "Dutch" },
  { value: "pl", label: "Polish" },
  { value: "ru", label: "Russian" },
  { value: "tr", label: "Turkish" },
  { value: "ar", label: "Arabic" },
  { value: "hi", label: "Hindi" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "zh", label: "Chinese" },
];

export const CEFR_OPTIONS: { value: string; label: string }[] = [
  { value: "A1", label: "A1" },
  { value: "A2", label: "A2" },
  { value: "B1", label: "B1" },
  { value: "B2", label: "B2" },
  { value: "C1", label: "C1" },
];

export interface FrameworkPresentation {
  emoji: string;
  /** Tailwind classes for the icon tile background + text. */
  tile: string;
  tagline: string;
}

export interface FrameworkForm {
  fields: FieldSpec[];
  /** Returns an error string if the assembled inputs are invalid, else null. */
  validate?: (inputs: StartInputs) => string | null;
  /** Post-process the assembled start_inputs (derive/override keys). Runs
   *  after buildStartInputs, before the run is posted. */
  transform?: (inputs: StartInputs) => StartInputs;
  /** When set, the framework can't be started from web yet (e.g. needs existing sets). */
  unavailableNote?: string;
}

export const PRESENTATION: Record<string, FrameworkPresentation> = {
  "learn-a-topic": {
    emoji: "📘",
    tile: "bg-blue-100 text-blue-700",
    tagline: "Understand new material from a source you upload or paste.",
  },
  "math-deep-dive": {
    emoji: "➗",
    tile: "bg-indigo-100 text-indigo-700",
    tagline: "Drop a problem, page, or topic name. Eddi reads the math and goes deep.",
  },
  "de-trainer": {
    emoji: "🇩🇪",
    tile: "bg-teal-100 text-teal-700",
    tagline: "Trainiere Deutsch: Grammatik, Vokabeln, Lesen & Verstehen.",
  },
  "language-trainer": {
    emoji: "🗣️",
    tile: "bg-sky-100 text-sky-700",
    tagline: "Learn any language — grammar, vocab and skills, in immersion or your own language.",
  },
  "lecture-exercise-recap": {
    emoji: "🎓",
    tile: "bg-pink-100 text-pink-700",
    tagline: "Drop a week's lectures + exercises. Tap inline concepts to recap.",
  },
  "master-a-trait": {
    emoji: "🎯",
    tile: "bg-emerald-100 text-emerald-700",
    tagline: "Pick a trait or skill. Eddi decomposes it into principles and drills them.",
  },
  "combine-question-sets": {
    emoji: "🧩",
    tile: "bg-amber-100 text-amber-700",
    tagline: "Merge existing sets into one combined practice set.",
  },
  lecture: {
    emoji: "📄",
    tile: "bg-orange-100 text-orange-700",
    tagline: "Open your own lecture PDF, mark what to master, and Eddi builds a full study unit.",
  },
};

export const FALLBACK_PRESENTATION: FrameworkPresentation = {
  emoji: "✨",
  tile: "bg-brand-100 text-brand-700",
  tagline: "A custom generation framework.",
};

const FOCUS_OPTIONS = [
  { value: "", label: "Let Eddi decide" },
  { value: "Focus on background knowledge so I can follow the material.", label: "Build background" },
  { value: "Help me recap what I should already know.", label: "Recap basics" },
  { value: "Push me toward deep understanding.", label: "Go deep" },
  { value: "Set me up for the topic that comes next.", label: "Prep for what's next" },
];

const sourceValidate = (inputs: StartInputs): string | null => {
  const text = String(inputs.source_text ?? "").trim();
  const topic = String(inputs.source_topic ?? "").trim();
  const markedFile = Array.isArray(inputs.source_file_ids) && inputs.source_file_ids.length > 0;
  const task = String(inputs.task_text ?? "").trim();
  if (!text && !topic && !markedFile && !task)
    return "Paste some material, name a topic, or load a marked file to study.";
  return null;
};

// Shared lite-enrichment fields offered on the discussion-path frameworks: load
// an already-marked file (→ source_file_ids) and an optional exercise to solve.
const MARKED_FILE_FIELD: FieldSpec = {
  key: "source_file_ids",
  label: "…or load a file you marked",
  kind: "marked-file-picker",
  hint: "Eddi reads your marks + pulls a few figures, and tailors the set to them.",
};
const TASK_FIELD: FieldSpec = {
  key: "task_text",
  label: "An exercise to solve & practise (optional)",
  kind: "textarea",
  placeholder: "Paste a problem / exercise. Eddi solves it and adds a worked-solution card + practice.",
};

export const FORMS: Record<string, FrameworkForm> = {
  "learn-a-topic": {
    fields: [
      { key: "source_text", label: "Your material", kind: "source", placeholder: "Paste lecture notes, an article, a chapter… or upload a file." },
      { key: "source_topic", label: "…or just name a topic", kind: "text", placeholder: "e.g. Photosynthesis" },
      MARKED_FILE_FIELD,
      { key: "focus", label: "Focus", kind: "select", options: FOCUS_OPTIONS },
      {
        key: "rigour",
        label: "Rigour",
        kind: "select",
        default: "standard",
        options: [
          { value: "standard", label: "Standard" },
          { value: "deep", label: "Deep" },
        ],
        hint: "Deep hunts misconceptions and grades its own questions (slower).",
      },
      { key: "instructions", label: "Anything else for Eddi? (optional)", kind: "text", placeholder: "Emphasise X, skip Y…" },
      { key: "title", label: "Set name (optional)", kind: "text", placeholder: "Auto-named if blank" },
      { key: "include_plot_questions", label: "Add graph-reading questions", kind: "toggle", hint: "When the material has functions/graphs, Eddi renders plots and asks you to read them." },
      { key: "headteacher_v2", label: "Use the chip-rail planner (preview)", kind: "toggle", hint: "Newer interactive planner. Leave off for the classic topic picker." },
    ],
    validate: sourceValidate,
  },
  "math-deep-dive": {
    fields: [
      { key: "source_topic", label: "Topic", kind: "text", placeholder: "e.g. Cauchy sequences" },
      {
        key: "source_text",
        label: "…or the math itself",
        kind: "source",
        accept: ".pdf,.txt,.md,.docx,image/*",
        placeholder: "Paste a problem, theorem, derivation… or upload a page / photo.",
        hint: "PDF, DOCX, TXT or a photo of a page — Eddi reads the math.",
      },
      {
        key: "math_level",
        label: "Level",
        kind: "select",
        default: "undergrad",
        options: [
          { value: "high_school", label: "High school" },
          { value: "undergrad", label: "Undergraduate" },
          { value: "graduate", label: "Graduate" },
          { value: "research", label: "Research" },
        ],
      },
      MARKED_FILE_FIELD,
      TASK_FIELD,
      { key: "include_concept_graph", label: "Annotate prerequisites & next steps", kind: "toggle" },
      { key: "include_transfer", label: "Include transfer questions (preview)", kind: "toggle" },
    ],
    validate: sourceValidate,
  },
  "lecture-exercise-recap": {
    fields: [
      { key: "lecture_text", label: "Lecture material", kind: "source", required: true, placeholder: "Paste this week's lecture notes/slides… or upload a file." },
      { key: "exercise_text", label: "Exercise material", kind: "source", required: true, placeholder: "Paste the matching problem set / exercises… or upload a file." },
      MARKED_FILE_FIELD,
      TASK_FIELD,
      { key: "include_plot_questions", label: "Add graph-reading questions", kind: "toggle", hint: "Renders plots from the material's functions and asks you to read them." },
    ],
    validate: (inputs) => {
      if (!String(inputs.lecture_text ?? "").trim()) return "Lecture material is required.";
      if (!String(inputs.exercise_text ?? "").trim()) return "Exercise material is required.";
      return null;
    },
  },
  "master-a-trait": {
    fields: [
      { key: "trait_name", label: "Trait or skill", kind: "text", required: true, placeholder: "e.g. Writing clear commit messages" },
      { key: "trait_description", label: "Context (optional)", kind: "textarea", placeholder: "What does mastery look like for you?" },
      { key: "source_text", label: "Source material (optional)", kind: "source", placeholder: "Paste any reference material… or upload a file." },
      MARKED_FILE_FIELD,
      { key: "include_worked_examples", label: "Include worked examples", kind: "toggle", default: true },
      { key: "include_cheatsheet", label: "Include a cheatsheet", kind: "toggle", default: true },
      { key: "include_reading_passages", label: "Include reading passages", kind: "toggle" },
    ],
    validate: (inputs) =>
      String(inputs.trait_name ?? "").trim() ? null : "Name the trait or skill to master.",
  },
  "de-trainer": {
    fields: [
      { key: "source_topic", label: "Thema / Schwerpunkt", kind: "text", placeholder: "z. B. Akkusativ, Konjunktiv II" },
      { key: "source_text", label: "…oder Material", kind: "source", placeholder: "Quelltext, Übung, Lesetext einfügen… oder Datei hochladen." },
      { key: "learning_topic", label: "Themengebiet (optional)", kind: "text", placeholder: "z. B. Reisen, Arbeit" },
    ],
    validate: sourceValidate,
  },
  "language-trainer": {
    fields: [
      {
        key: "target_language",
        label: "Language to learn",
        kind: "select",
        required: true,
        default: "es",
        options: SUPPORTED_LANGUAGES,
      },
      {
        key: "cefr_level",
        label: "Level (CEFR)",
        kind: "select",
        default: "B1",
        options: CEFR_OPTIONS,
      },
      {
        key: "explanation_mode",
        label: "Explanations",
        kind: "select",
        default: "native",
        hint: "My language adapts with your level: A1–A2 fully in your language, B1–B2 one simple target-language sentence then the recap in yours, C1+ fully immersed. At A1–A2, Immersion also keeps explanations in your language until you can read them.",
        options: [
          { value: "native", label: "My language (adapts as you level up)" },
          { value: "immersion", label: "Immersion (target language)" },
        ],
      },
      { key: "source_topic", label: "Topic / focus (optional)", kind: "text", placeholder: "e.g. past tense, travel vocabulary" },
      { key: "learning_topic", label: "Example domain (optional)", kind: "text", placeholder: "e.g. cooking, football" },
      { key: "pure_drills", label: "Practice drills only", kind: "toggle", hint: "Skip reading passages and summary cards — just hands-on drills (gap-fill, conjugation, word order, transformation…)." },
      { key: "include_picture_vocab", label: "🖼️ Picture vocabulary", kind: "toggle", hint: "Generate pictures of concrete words and name or describe them. Adds a small image cost." },
      {
        key: "source_text",
        label: "Source material (optional)",
        kind: "source",
        accept: ".pdf,.txt,.md,.docx,image/*",
        placeholder: "Paste a text, dialogue, or vocabulary list… or upload a file.",
        hint: "PDF, DOCX, TXT or a photo of a page — Eddi reads it into the material above.",
      },
    ],
    // Language alone is enough to start (matches iOS).
    validate: (inputs) =>
      String(inputs.target_language ?? "").trim() ? null : "Pick a language to learn.",
    transform: (inputs) => {
      const out = { ...inputs };
      const mode = String(out.explanation_mode ?? "native");
      // Immersion ⇒ content + explanations in the target language (matches iOS).
      if (mode === "immersion" && out.target_language) out.language = out.target_language;
      // Backend reads CEFR structurally via `level`.
      if (out.cefr_level) {
        out.level = out.cefr_level;
        delete out.cefr_level;
      }
      return out;
    },
  },
  "combine-question-sets": {
    fields: [],
    unavailableNote:
      "Combining sets needs your existing Library, which arrives in the next web phase. For now, use the iOS app to combine sets.",
  },
};

export function presentationFor(slug: string): FrameworkPresentation {
  return PRESENTATION[slug] ?? FALLBACK_PRESENTATION;
}
